import { CurrentGameParams } from "@lib/titan2-slingo/dist/api/currentgame/CurrentGameParams";
import { GameState } from "@lib/titan2-slingo/dist/api/model/enum/GameState";
import { SpinParams } from "@lib/titan2-slingo/dist/api/spin/SpinParams";
import { SlingoEvent } from "@lib/titan2-slingo/dist/game/controller/event/SlingoEvent";
import { SlingoController } from "@lib/titan2-slingo/dist/game/controller/SlingoController";
import { CommonGameEvent } from "@lib/titan2/dist/game/controller/event/CommonGameEvent";
import { Renderer } from "@lib/titan2/dist/game/renderer/Renderer";
import { Logger } from "@lib/titan2/dist/util/log/Logger";
import { ExtendedCollectParams } from "../../api/collect/ExtendedCollectParams";
import { FeatureStateType, FreeSpinReelState, FeatureType } from "../model/GameModel";
import { GameEvent, FreeSpinReelControllerEvent } from "./event/GameEvent";
import { GameWinController } from "./GameWinController";
import { StakeToSpinParams } from "@lib/titan2-slingo/dist/api/stakeToSpin/StakeToSpinParams";
import { GameTicketController } from "./GameTicketController";
import { Service } from "@lib/titan2-slingo/dist/api/Service";
import { GameReplayParser } from "../../api/replay/GameReplayParser";
import { FeatureTransitionEvents } from "./FeatureTransitionController";
import { FeatureSelectorControllerEvents } from "./FeatureSelectorController";
import { ArcadeWheelEvent } from "@lib/titan2-slingo/dist/game/view/abstract/display/ArcadeWheel";
import { Slingo } from "@lib/titan2-slingo/dist/api/Slingo";
import "../../api/ApiPawnshop";
import { PawnshopSlingo } from "../../api/PawnshopSlingo";
import { LogParams } from "../../api/log/LogParams";
import { MainTransitionEvents } from "../view/abstract/display/GameMainTransition";
import { FactoryManager } from "@lib/titan2/dist/game/factory/FactoryManager";
/**
 * GameController.ts
 */
export class GameController extends SlingoController {
    constructor(factory) {
        super(factory);
        Service.setReplayParserClass(GameReplayParser);
        so.Wrapper.getInstance()?.getModel()?.setEnginePathOverride?.(`slingo/${Slingo.gameType}`);
        Slingo.gameId = FactoryManager.getInstance().getFactory().getWrapperConfig().getGame().getId(); // set gameID to allow for multi-rtp
    }
    createWinController() {
        this.winController = new GameWinController(this.baseFactory);
    }
    addEventListeners() {
        super.addEventListeners();
        // this.registerGame(TicketViewEvent.SLINGO_TICKET_ANIMATION_COMPLETE, this.handleSlingo);
        this.registerGame(FreeSpinReelControllerEvent.FREE_SPIN_TRANSITION_OUT_COMPLETE, this.updateSpinState);
        this.registerGame(FreeSpinReelControllerEvent.FREE_SPIN_GAME_OVER_TRANSITION_OUT_COMPLETE, this.handleComplete);
        this.registerGame(MainTransitionEvents.BLACKOUT_MID_POINT, this.handleComplete);
        this.registerGame(FeatureSelectorControllerEvents.SEND_COLLECT_CHOICE, this.handleCollectChoice);
        this.registerGame(GameEvent.USER_CHOICE_MADE, this.handleUserChoiceMade);
    }
    handleUserChoiceMade(event) {
        const payload = event.getCurrentTarget();
        this.model.addUserchoice(payload.choice);
        this.makeLogRequest();
    }
    makeLogRequest() {
        if (!this.model.getGameInstanceId()) {
            console.warn("Attempted Log Request with no Game Instance Id");
            return;
        }
        if (!this.model.getUserChoices().length) {
            console.warn("Attempted Log Request with no User Choices");
            return;
        }
        PawnshopSlingo.log(new LogParams(this.error.hasAcknowledged(), this.model.getGameInstanceId(), this.model.getUserChoices()), this.handleLogRequest, this);
    }
    handleLogRequest(result) {
    }
    handleSpinRequest() {
        if (!Service.isReplay()) {
            super.handleSpinRequest();
        }
        else {
            if (Service.isReplayStopping()) {
                this.stopReplay();
            }
            else if (Service.isReplaySpinsComplete() && Service.isReplayStakeToSpinsComplete()) {
                this.dispatchGame(ArcadeWheelEvent.HIDE);
                this.handleSendCollectRequest();
            }
            else if (this.model.isStakeToSpin()) {
                this.makeStakeToSpinRequest(new StakeToSpinParams(this.error.hasAcknowledged(), this.model.getRoomName()));
            }
            else if (this.model.isSpinActionAllowed()) {
                this.dispatchGame(ArcadeWheelEvent.HIDE);
                this.makeSpinRequest(new SpinParams(this.error.hasAcknowledged(), this.model.getRoomName()));
            }
        }
    }
    updateGameUpdate(update) {
        const userChoices = update.getClientData()?.getUserChoices();
        if (userChoices !== null) {
            this.model.setUserChoices(userChoices);
        }
        super.updateGameUpdate(update);
    }
    updateResponsibleGaming() {
        let progress = this.model.getGameProgress();
        if (progress) {
            let spinsRemaining = progress.getPurchasableSpinsRemaining();
            let spinsAvailable = progress.getPurchasableSpinsAvailable();
            this.responsibleGaming.update(spinsRemaining, spinsAvailable);
        }
    }
    updateSpinState() {
        if ((this.model.getFreeSpinReelState() === FreeSpinReelState.FREE_SPIN_REEL && this.model.getGameProgress().getFreeSpinsRemaining() === 0)) {
            this.dispatchGame(FreeSpinReelControllerEvent.FREE_SPIN_TRANSITION_OUT_START);
        }
        else if (this.model.isPurchaseSpin() && Service.isReplay()) {
            super.updateSpinState();
            this.callAfter(1000, () => {
                this.dispatchGame(CommonGameEvent.SPIN_REQUEST);
            });
        }
        else {
            super.updateSpinState();
        }
    }
    nextGame() {
        if (this.isRequestPermitted()) {
            // Slingo.setGameId(this.model.getGameId());
            let parameters = new CurrentGameParams(this.model.getRoomName());
            Slingo.getCurrentGame(parameters, this.handleCurrentGameResult, this);
        }
    }
    makePurchaseEntryRequest(parameters) {
        Slingo.purchaseEntry(parameters, this.handlePurchaseEntryResult, this);
    }
    makeSpinRequest(parameters) {
        Slingo.spin(parameters, this.handleSpinResult, this);
    }
    makeStakeToSpinRequest(parameters) {
        Slingo.stakeToSpin(parameters, this.handleStakeToSpinResult, this);
    }
    makeChooseCellRequest(parameters) {
        Slingo.chooseCell(parameters, this.handleChooseCellResult, this);
    }
    makeCollectRequest(pick) {
        if (pick !== undefined) {
            Slingo.collect(new ExtendedCollectParams(this.error.hasAcknowledged(), pick), this.handleFeatureRequestEnd, this);
        }
        else if (this.model.getFeatureState() === FeatureStateType.DISABLED) {
            Slingo.collect(new ExtendedCollectParams(this.error.hasAcknowledged()), this.handleCollectResult, this);
        }
        else {
            Slingo.collect(new ExtendedCollectParams(this.error.hasAcknowledged()), this.handleFeatureRequestEnd, this);
        }
    }
    createTicketController() {
        this.ticketController = new GameTicketController(this.baseFactory);
    }
    handleLimitReached() {
        this.responsibleGaming.completeGame(this.model.getGameInstanceId());
        this.dispatchGame(CommonGameEvent.RESPONSIBLE_GAMING_SET_TRIGGER);
        this.responsibleGaming.reset();
        this.callAfter(350, this.handleCollectRequest);
    }
    handleSpinResult(result) {
        let responseCode = result.getResponseCode();
        if (this.isSuccess(responseCode)) {
            this.dispatchGame(CommonGameEvent.WAGER_RESPONSE);
            let data = result.getData();
            let update = data.getGameUpdate();
            this.updateSpinPriceTotal();
            this.updateGameUpdate(update);
            this.updateClient(update);
            this.updateResponsibleGaming();
            this.model.setSpinResultResolving(true);
            this.model.setChanceGamesData(data.getChanceGame());
            this.updateFeatureType();
            this.model.setChanceGamesData(data.getChanceGame());
            this.dispatchGame(SlingoEvent.SPIN_RESULT);
        }
        else {
            this.handleErrorResponse(responseCode);
        }
    }
    handleChooseCellResult(result) {
        let responseCode = result.getResponseCode();
        if (this.isSuccess(responseCode)) {
            let data = result.getData();
            let update = data.getGameUpdate();
            this.updateGameUpdate(update);
            this.updateClient(update);
            this.updateResponsibleGaming();
            this.model.setChanceGamesData(data.getChanceGame());
            this.updateFeatureType();
            this.dispatchGame(SlingoEvent.CHOOSE_CELL_RESULT);
        }
        else {
            this.handleErrorResponse(responseCode);
        }
    }
    handleStakeToSpinResult(result) {
        let responseCode = result.getResponseCode();
        if (this.isSuccess(responseCode)) {
            let data = result.getData();
            this.model.setChanceGamesData(data.getChanceGame());
            this.updateFeatureType();
            super.handleStakeToSpinResult(result);
        }
        else {
            this.handleErrorResponse(responseCode);
        }
    }
    updateGame() {
        if (this.model.isPurchaseEntry()) {
            Logger.verbose("----PURCHASE ENTRY----");
            if (!this.model.getFeatureState()?.isInProgress()) {
                this.dispatchGame(CommonGameEvent.PURCHASE_ENTRY);
            }
        }
        else if (this.model.isComplete()) {
            Logger.verbose("----COMPLETE----");
            this.updateTotalWin();
            this.determineCompleteBehaviour();
        }
        else if (!this.model.getSpinResultResolving()) {
            Logger.verbose("----OTHER----");
            this.updateSpinState();
        }
        Logger.verbose("----NONE?----");
    }
    checkCollectAllowed() {
        // GD-8024 in case there is a COLLECT request while in the CHOOSE_CELL state
        if (this.model.getState() === GameState.CHOOSE_CELL) {
            this.handleErrorResponse(null, null);
            Renderer.getInstance().getStage().interactiveChildren = false; //Anything in the Pixi graph will not be interactive
        }
    }
    updateGameRecovery(recovery) {
        let progress = this.model.getGameProgress();
        this.model.setFirstGame(false);
        this.model.setFreeGame(/*recovery.isFreeGame()*/ false);
        this.model.setRecovery(recovery);
        this.model.setFreeSpinIndex(progress.getFreeSpinsRemaining());
        this.ticketController.updateRecovery();
        this.updateTotalWin();
        const freeSpinSymbolsOnReel = this.model.getSpinResult()?.getReelSequence().getFreeSpinIndexes().length ?? 0;
        const recoveryFreeSpins = this.model.isChooseCell() ? progress.getFreeSpinsRemaining() - freeSpinSymbolsOnReel : progress.getFreeSpinsRemaining();
        if (recoveryFreeSpins > 0 && !Service.isReplay()) {
            this.handleFreeSpinRecovery();
        }
        else if (progress.getSpinsRemaining() > 0 || (progress.getPurchaseSpinsUsed() === 0 && this.model.isChooseCell())) {
            this.handleStandardSpinRecovery();
        }
        else if (progress.getPurchasableSpinsAvailable() > 0) {
            this.handlePurchaseSpinRecovery();
        }
        this.ticketController.updateDabRecovery();
    }
    handleFreeSpinRecovery() {
        super.handleFreeSpinRecovery();
        this.model.setFreeSpinReelState(FreeSpinReelState.FREE_SPIN_REEL);
        this.dispatchGame(GameEvent.FREE_SPIN_REELS_RECOVER);
    }
    handleStandardSpinRecovery() {
        super.handleStandardSpinRecovery();
    }
    handleCollectRequest() {
        if (this.isRequestPermitted()) {
            this.updateFeatureType();
            this.winController.reset();
            this.model.setCollected(true);
            this.makeCollectRequest();
        }
    }
    updateFeatureType() {
        const featureType = this.model.featureTypeFromChanceGameConfigId(this.model.getChanceGameData()?.getGameConfigId() ?? "");
        this.model.setFeatureType(featureType);
        const matchedPatterns = this.model.getCurrentMatchedPatterns();
        if (this.model.getFeatureState() !== FeatureStateType.COLLECTED) {
            if (matchedPatterns >= 5) {
                this.model.setFeatureState(FeatureStateType.ENABLED);
            }
            else {
                this.model.setFeatureState(FeatureStateType.DISABLED);
            }
        }
    }
    handleFeatureRequestEnd(result) {
        const responseCode = result.getResponseCode();
        if (this.isSuccess(responseCode)) {
            if (result.getData()) {
                this.handleFeatureResult(result);
                this.runFeature();
            }
        }
        else {
            this.handleErrorResponse(responseCode);
        }
    }
    handleFeatureResult(result) {
        let responseCode = result.getResponseCode();
        if (this.isSuccess(responseCode)) {
            let data = result.getData();
            let update = data.getGameUpdate();
            this.model.setSlotsGameData(data.getSlotsGame());
            this.updateGameUpdate(update);
            this.updateClient(update);
            this.model.setChanceGamesData(data.getChanceGame());
            this.updateFeatureType();
        }
        else {
            this.handleErrorResponse(responseCode);
        }
    }
    handleCollectChoice(event) {
        this.winController.reset();
        this.model.setCollected(true);
        this.makeCollectRequest(event.getPayload().pick);
    }
    handleFullHouseAnimationComplete() {
        this.dispatchGame(FeatureSelectorControllerEvents.PRESENT_FEATURE_SELECTION);
    }
    handleComplete() {
        if (this.model.getFreeSpinReelState() === FreeSpinReelState.FREE_SPIN_REEL) {
            this.dispatchGame(FreeSpinReelControllerEvent.FREE_SPIN_GAME_OVER_TRANSITION_OUT_START);
            return;
        }
        let resultData = this.getResultData();
        if (resultData.isFullHouse() && this.model.getFeatureState() !== FeatureStateType.COLLECTED) {
            this.dispatchGame(SlingoEvent.FULL_HOUSE_ANIMATION_PHASE_ONE);
        }
        else if (this.model.getFeatureType() !== FeatureType.NONE && this.model.getFeatureState() !== FeatureStateType.COLLECTED) {
            this.runFeature();
        }
        else if (this.model.getFeatureType() === FeatureType.NONE) {
            this.dispatchGame(SlingoEvent.WIN_DISPLAY_START);
        }
    }
    runFeature() {
        this.model.setFeatureState(FeatureStateType.IN_PROGRESS);
        this.updateResultData();
        this.dispatchGame(CommonGameEvent.BONUS_WON);
        switch (this.model.getFeatureType()) {
            case FeatureType.GREEN_FISH:
                this.dispatchGame(FeatureTransitionEvents.TRANSITION_TO_GREEN_FISH);
                break;
            case FeatureType.RED_FISH:
                this.dispatchGame(FeatureTransitionEvents.TRANSITION_TO_RED_FISH);
                break;
            case FeatureType.PURPLE_FISH:
                this.dispatchGame(FeatureTransitionEvents.TRANSITION_TO_PURPLE_FISH);
                break;
            case FeatureType.FISH_FOOD:
                this.dispatchGame(FeatureTransitionEvents.TRANSITION_TO_FISH_FOOD);
                break;
            case FeatureType.GOLD_FISH:
                this.dispatchGame(FeatureTransitionEvents.TRANSITION_TO_GOLD_FISH);
                break;
            default:
                throw new Error("Unkown Feature: " + this.model.getFeatureType());
        }
    }
    updateResultData() {
        this.getResultData();
    }
    getResultData() {
        const resultData = this.model.getResultData();
        const totalWin = this.model.getTotalWin();
        const payoutConfig = this.model.getPayoutConfig();
        const totalPatterns = payoutConfig.getPatterns().length;
        const progress = this.model.getGameProgress();
        const matchedPatterns = progress?.getMatchedPatterns() ?? 0;
        const stake = this.model.getCurrentStake();
        const totalStaked = this.model.getTotalStaked();
        const bigWinThreshold = this.model.getBigWinThreshold();
        const purchasedSpinTotal = this.model.getPurchasedSpinTotal();
        const bigWinTotalStake = this.model.isUsePurchaseSpinTotal() ? stake + purchasedSpinTotal : stake;
        const currentGame = this.model.getCurrentGame();
        const gameConfig = currentGame.getConfig();
        const gameConfigData = gameConfig.getGameConfig();
        const celebrateStake = stake * gameConfigData.getCelebrateWinThreshold();
        resultData.setSlingoWin(totalWin);
        resultData.setFullHouse(matchedPatterns == totalPatterns);
        resultData.setNoWin(totalWin <= 0);
        resultData.setLowWin(totalWin < totalStaked);
        resultData.setSmallWin(celebrateStake >= totalWin);
        resultData.setWin2x(stake * 2 <= totalWin);
        resultData.setBigWin(bigWinTotalStake * bigWinThreshold <= totalWin);
        resultData.setWin25x(stake * 25 <= totalWin);
        return resultData;
    }
}
//# sourceMappingURL=GameController.js.map